import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from './common/ToastContainer';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  section: string;
}

const faqData: FAQItem[] = [
  // Section A — About SheCare
  {
    id: 'what-is-shecare',
    section: 'About SheCare',
    question: 'What is SheCare?',
    answer: 'SheCare is a guidance-first women\'s health consultation platform. We help women and families talk to qualified doctors for clarity, reassurance, and next steps — especially when living abroad or without insurance.'
  },
  {
    id: 'is-shecare-hospital',
    section: 'About SheCare',
    question: 'Is SheCare a hospital or clinic?',
    answer: 'No. SheCare is not a hospital or emergency service. We provide online consultations focused on education, reassurance, and guidance.'
  },
  {
    id: 'is-shecare-for-emergencies',
    section: 'About SheCare',
    question: 'Is SheCare for emergencies?',
    answer: 'No. If you are experiencing severe pain, heavy bleeding, chest pain, or any medical emergency, please seek immediate local medical care.'
  },
  
  // Section B — Consultations
  {
    id: 'what-happens-during-consultation',
    section: 'Consultations',
    question: 'What happens during a consultation?',
    answer: 'You\'ll speak with a qualified doctor who will listen to your concerns, explain what may be happening, and guide you on next steps — including when to see a local doctor.'
  },
  {
    id: 'can-i-get-diagnosis',
    section: 'Consultations',
    question: 'Can I get a diagnosis or medication?',
    answer: 'Depending on location and licensing, SheCare consultations may not include diagnosis, prescriptions, or medication. Our focus is helping you understand your situation and decide what to do next safely.'
  },
  {
    id: 'use-before-local-doctor',
    section: 'Consultations',
    question: 'Can I use SheCare before seeing a local doctor?',
    answer: 'Yes. Many users use SheCare to prepare for an in-person visit — to know what questions to ask and what symptoms matter.'
  },
  
  // Section C — Insurance & Pricing
  {
    id: 'need-insurance',
    section: 'Insurance & Pricing',
    question: 'Do I need insurance to use SheCare?',
    answer: 'No. SheCare is pay-per-session and does not require insurance.'
  },
  {
    id: 'consultation-cost',
    section: 'Insurance & Pricing',
    question: 'How much does a consultation cost?',
    answer: 'Prices are shown clearly before booking. We aim to keep consultations affordable and accessible.'
  },
  
  // Section D — Location & Licensing
  {
    id: 'use-outside-home-country',
    section: 'Location & Licensing',
    question: 'Can I use SheCare if I live outside my home country?',
    answer: 'Yes. SheCare is designed for people living abroad. The scope of consultation may vary depending on doctor licensing and your location.'
  },
  {
    id: 'book-for-family',
    section: 'Location & Licensing',
    question: 'Can I book for my parents or family members?',
    answer: 'Yes. You may book a consultation for a family member, including parents living in another country.'
  },
  
  // Section E — Safety & Privacy
  {
    id: 'doctors-qualified',
    section: 'Safety & Privacy',
    question: 'Are the doctors qualified?',
    answer: 'Yes. All doctors on SheCare are licensed professionals in their respective jurisdictions.'
  },
  {
    id: 'information-private',
    section: 'Safety & Privacy',
    question: 'Is my information private?',
    answer: 'Yes. We take privacy seriously and handle your data securely according to our Privacy Policy.'
  }
];

const FAQ: React.FC = () => {
  const toast = useToast();
  const [openId, setOpenId] = useState<string | null>(null);

  // Get unique sections
  const sections = Array.from(new Set(faqData.map(item => item.section)));

  const toggleQuestion = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  // Scroll to question if URL has hash
  React.useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setOpenId(hash);
      }
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F9FF] via-white to-[#FFF1F2]">
      {/* Header */}
      <div className="bg-white border-b border-[#334155]/10">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#334155] mb-2">
                Frequently Asked Questions
              </h1>
              <p className="text-[#334155]/60">
                Everything you need to know about SheCare
              </p>
            </div>
            <button
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 text-[#334155] hover:bg-[#F0F9FF] rounded-lg transition-colors"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {sections.map((section) => (
          <div key={section} className="mb-12">
            <h2 className="text-xl font-semibold text-[#334155] mb-6 pb-2 border-b border-[#334155]/10">
              {section}
            </h2>
            
            <div className="space-y-4">
              {faqData
                .filter(item => item.section === section)
                .map((item) => (
                  <div
                    key={item.id}
                    id={item.id}
                    className="bg-white rounded-xl border border-[#334155]/10 overflow-hidden transition-all hover:shadow-md"
                  >
                    <button
                      onClick={() => toggleQuestion(item.id)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-[#F0F9FF] transition-colors"
                    >
                      <span className="font-medium text-[#334155] pr-4">
                        {item.question}
                      </span>
                      {openId === item.id ? (
                        <ChevronUp className="w-5 h-5 text-[#38BDF8] flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#334155]/40 flex-shrink-0" />
                      )}
                    </button>
                    
                    {openId === item.id && (
                      <div className="px-6 pb-4 pt-2">
                        <p className="text-[#334155]/70 leading-relaxed">
                          {item.answer}
                        </p>
                        <div className="mt-3 pt-3 border-t border-[#334155]/5">
                          <button
                            onClick={() => {
                              const url = `${window.location.origin}/faq#${item.id}`;
                              navigator.clipboard.writeText(url);
                              toast.success('Link copied!', 'You can now share this FAQ link');
                            }}
                            className="text-sm text-[#38BDF8] hover:text-[#0EA5E9] transition-colors"
                          >
                            📋 Copy link to this question
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        ))}

        {/* Still have questions? */}
        <div className="mt-16 bg-gradient-to-r from-[#F0F9FF] to-[#FFF1F2] rounded-2xl p-8 text-center">
          <h3 className="text-xl font-semibold text-[#334155] mb-3">
            Still have questions?
          </h3>
          <p className="text-[#334155]/70 mb-6">
            We're here to help. Book a consultation to speak with a qualified doctor.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-3 bg-[#38BDF8] text-white rounded-xl font-medium hover:bg-[#0EA5E9] shadow-lg shadow-[#38BDF8]/30 transition-all"
          >
            Book a Consultation
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-[#334155]/10 py-6">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-[#334155]/50">
          <p>
            <a href="/terms" className="hover:text-[#38BDF8] transition-colors">Terms & Conditions</a>
            {' • '}
            <a href="/privacy" className="hover:text-[#38BDF8] transition-colors">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
